import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Chip, Container, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { listProducts, deleteProduct } from '../services/Common/products.service';
// import Page from '../data/Page';
import palette from '../theme/palette';
import { toast } from 'react-toastify';

export default function ProductsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  const [rowCount, setRowCount] = useState(0);

  const columns = useMemo(() => ([
    { field: 'sku', headerName: 'SKU', minWidth: 150, flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'name', headerName: 'Name', minWidth: 200, flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'price', headerName: 'Price', minWidth: 120, flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'stock', headerName: 'Stock', minWidth: 100, align: 'center', headerAlign: 'center' },
    { field: 'status', headerName: 'Status', minWidth: 120, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        const value = (params.value || '').toString().toLowerCase();
        const label = value ? value.charAt(0).toUpperCase() + value.slice(1) : '-';
        return <Chip label={label} color={value === 'active' ? 'success' : 'default'} size='small'/>;
      } },
    { field: 'actions', headerName: 'Actions', minWidth: 260, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction='row' spacing={1}>
          <Button size='small' variant='contained' onClick={() => navigate(`/products/update/${params.id}`)}>View</Button>
          <Button size='small' variant='contained' onClick={() => navigate(`/products/update/${params.id}`)}>Edit</Button>
          <Button size='small' variant='contained' color='error' onClick={() => onDelete(params.id)}>Delete</Button>
        </Stack>
      )
    }
  ]), [navigate]);

  const onDelete = async (id) => {
    const ok = window.confirm('Delete this product?');
    if (!ok) return;
    try {
      await deleteProduct(id);
      setRows(prev => prev.filter(r => r._id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    }
  };

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const [sortField, sortDir] = sort.split(':');
      const params = {
        page: page + 1,
        limit: pageSize,
        q: q || undefined,
        categoryId: categoryId || undefined,
        minPrice: minPrice || undefined,
        maxPrice: maxPrice || undefined,
        status: status || undefined,
        sort: sortDir === 'desc' ? `-${sortField}` : sortField
      };
  const res = await listProducts(params);
  const items = res.items || res.results || res || [];
  const total = res.total || res.pagination?.total || items.length;
      setRows(items);
      setRowCount(total);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [sort, page, pageSize, q, categoryId, minPrice, maxPrice, status]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <>
      <Helmet><title>Products | Trooper</title></Helmet>
      <Container>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
          <Typography variant='h4'>Products</Typography>
          <Button variant='contained' onClick={() => navigate('/products/create')}>New Product</Button>
        </Stack>

        <Stack direction='row' spacing={2} mb={2}>
          <TextField size='small' label='Search (name/sku)' value={q} onChange={e => setQ(e.target.value)} onKeyDown={(e)=> e.key==='Enter' && fetchRows()} />
          <TextField size='small' label='Category ID' value={categoryId} onChange={e => setCategoryId(e.target.value)} />
          <TextField size='small' label='Min Price' type='number' value={minPrice} onChange={e => setMinPrice(e.target.value)} />
          <TextField size='small' label='Max Price' type='number' value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
          <Select size='small' displayEmpty value={status} onChange={e => setStatus(e.target.value)}>
            <MenuItem value=''>All Status</MenuItem>
            <MenuItem value='active'>Active</MenuItem>
            <MenuItem value='inactive'>Inactive</MenuItem>
          </Select>
          <Select size='small' value={sort} onChange={e => setSort(e.target.value)}>
            <MenuItem value='createdAt:desc'>Newest</MenuItem>
            <MenuItem value='createdAt:asc'>Oldest</MenuItem>
            <MenuItem value='price:asc'>Price: Low-High</MenuItem>
            <MenuItem value='price:desc'>Price: High-Low</MenuItem>
          </Select>
          <Button variant='outlined' onClick={fetchRows}>Apply</Button>
        </Stack>

        <div style={{ height: '75vh', width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(r) => r._id}
            pagination
            paginationMode='server'
            pageSizeOptions={[5,10,25,50]}
            rowCount={rowCount}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          {rows.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', color: palette.grey[600], fontSize: '18px', mt: 2 }}>No Row Available</Box>
          )}
        </div>
      </Container>
    </>
  );
}
